import {
  alpha,
  Box,
  Card,
  Chip,
  Container,
  CircularProgress,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, Receipt, TrendingUp } from "@mui/icons-material";
import { getTransactions, getTransactionView } from "apis";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { fetchTransactions, FinanceState } from "store/features/finance";
import { AuthState } from "store/features/auth";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const Transactions: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const dispatch = useDispatch();
  const {
    transactions: { loaded, items },
  } = useSelector<RootState, FinanceState>((store) => store.finance);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchFn =
      role === "NURSERY_ADMIN" ? getTransactionView : getTransactions;
    fetchFn()
      .then(({ data }) => dispatch(fetchTransactions(data)))
      .finally(() => setLoading(false));
  }, [dispatch, role]);

  // Group transactions by month
  const grouped = useMemo(() => {
    const groups: Record<string, any[]> = {};
    items.forEach((item: any) => {
      const date = item.createdAt || item.date;
      const key = dayjs(date).format("MMMM YYYY");
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [items]);

  const monthKeys = Object.keys(grouped);

  return (
    <>
      {/* Header */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => navigate("/finance")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <TrendingUp sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("posts.transactions")}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            ml: 0.5,
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            fontWeight: 600,
          }}
        />
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={8}
          color="text.secondary"
        >
          <Receipt sx={{ fontSize: 64, mb: 2, color: "action.disabled" }} />
          <Typography variant="h6" gutterBottom>
            {t("finance.no_bill")}
          </Typography>
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {monthKeys.map((month) => (
            <Box key={month} mb={3}>
              {/* Month header */}
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: 11,
                  mb: 1,
                  display: "block",
                }}
              >
                {month}
              </Typography>

              <Card
                variant="outlined"
                sx={{ borderRadius: 2.5, overflow: "hidden" }}
              >
                {grouped[month].map((item: any, idx: number) => {
                  const date = item.createdAt || item.date;
                  const heading = item.heading || item.title || "Transaction";
                  const ref = item.ref || item.id;
                  const amount = item.amount;
                  const status = item.status;
                  const symbol = item.currency?.symbol || "";
                  const isPaid = status === 1;

                  return (
                    <Box key={ref}>
                      {idx > 0 && <Divider />}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 2.5,
                          py: 1.5,
                          gap: 2,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        {/* Date */}
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            lineHeight={1}
                            color="primary.main"
                          >
                            {dayjs(date).format("DD")}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontSize={9}
                            color="text.secondary"
                            lineHeight={1}
                          >
                            {dayjs(date).format("MMM")}
                          </Typography>
                        </Box>

                        {/* Details */}
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {heading}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            noWrap
                          >
                            Ref: {ref}
                          </Typography>
                        </Box>

                        {/* Amount */}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={isPaid ? "success.main" : "error.main"}
                          sx={{ flexShrink: 0 }}
                        >
                          {symbol}
                          {Number(amount).toLocaleString()}
                        </Typography>

                        {/* Status */}
                        {status !== undefined && (
                          <Chip
                            label={
                              isPaid ? t("labels.paid") : t("labels.unpaid")
                            }
                            color={isPaid ? "success" : "error"}
                            size="small"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Card>
            </Box>
          ))}
        </Container>
      )}
    </>
  );
};

export default Transactions;
